import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Stack,
  Group,
  Badge,
  Avatar,
  Text,
  Title,
  Radio,
  Flex,
  ActionIcon,
  LoadingOverlay,
  Autocomplete,
} from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { HiArrowLeft } from 'react-icons/hi';
import { IoMdClose } from 'react-icons/io';
import { isMobile } from 'react-device-detect';
import { useQuery } from '@tanstack/react-query';
import AsyncSelect from 'react-select/async';
import { type SingleValue } from 'react-select';
import StarRatingButton from '@components/elements/StarRatingButton/StarRatingButton';
import QueryKeys from '@constants/queryKeys';
import { ONE_DAY } from '@constants/various';
import useNotification from '@hooks/useNotification';
import defaultAvatar from '@assets/default-avatar.png';
import {
  saveEndorsement,
  searchCommonBrands,
} from '@company/services/company/api';
import { fetchWorkerFilters } from '@company/services/worker/api';
import {
  EndorsementFormData,
  EndorsementPayload,
  EventsNumber,
  WorkerSuggestion,
} from '@company/types/endorsement.type';
import IWorkerFiltersData from '@company/types/workerFilters.type';

type WorkerOption = WorkerSuggestion & { value: number; label: string };

interface EndorsementModalProps {
  show: boolean;
  onHide: () => void;
  preselectedWorker?: {
    id: number;
    fullName: string;
    email: string;
    profileImageUrl: string;
  };
}

const EVENTS_NUMBER_OPTIONS = [
  { value: EventsNumber.N_1_2, label: '1-2' },
  { value: EventsNumber.N_3_6, label: '3-6' },
  { value: EventsNumber.N_7_9, label: '7-9' },
  { value: EventsNumber.N_10_PLUS, label: '10+' },
];

function EndorsementModal({
  show,
  onHide,
  preselectedWorker,
}: EndorsementModalProps) {
  const {
    showSuccessNotification,
    showWarningNotification,
    showErrorNotification,
  } = useNotification();
  const [step, setStep] = useState<1 | 2>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerSuggestion | null>(
    null
  );
  const [emailSearchInput, setEmailSearchInput] = useState('');
  const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);

  const { data: filtersData } = useQuery<IWorkerFiltersData>({
    queryKey: [QueryKeys.WORKER_FILTERS],
    queryFn: fetchWorkerFilters,
    staleTime: Infinity,
    gcTime: ONE_DAY,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    watch,
    reset,
  } = useForm<EndorsementFormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      fullName: '',
      companyName: '',
      reviewDescription: '',
      photoIsRepresentative: true,
      performance: undefined,
      isEndorsedManager: false,
      sharedEventsNumber: EventsNumber.N_1_2,
      sharedEventsType: [],
      endorsedTraits: [],
    },
  });

  const watchedEventTypes = watch('sharedEventsType');
  const watchedTraits = watch('endorsedTraits');

  // Set preselected worker on mount
  useEffect(() => {
    if (preselectedWorker && show) {
      const worker: WorkerSuggestion = {
        id: preselectedWorker.id,
        name: preselectedWorker.fullName,
        email: preselectedWorker.email,
        profileImageUrl: preselectedWorker.profileImageUrl,
        alreadyEndorsed: false,
      };
      setSelectedWorker(worker);
      setValue('workerId', worker.id);
      setValue('fullName', worker.name);
      setValue('email', worker.email);
      setEmailSearchInput(worker.email);
    }
  }, [preselectedWorker, show, setValue]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      reset();
      setStep(1);
      setSelectedWorker(null);
      setEmailSearchInput('');
    }
  }, [show, reset]);

  const handleWorkerSelect = (option: SingleValue<WorkerOption>) => {
    if (!option) {
      setSelectedWorker(null);
      setValue('workerId', undefined);
      setValue('fullName', '');
      setValue('email', '');
      setValue('photoIsRepresentative', undefined);
      return;
    }

    if (option.alreadyEndorsed) {
      showWarningNotification(
        'You have already endorsed this worker. Please select another worker.',
        { title: 'Already endorsed' }
      );
      return;
    }

    const worker: WorkerSuggestion = {
      id: option.value,
      name: option.label,
      email: option.email,
      profileImageUrl: option.profileImageUrl,
      alreadyEndorsed: option.alreadyEndorsed,
    };

    setSelectedWorker(worker);
    setValue('workerId', worker.id);
    setValue('fullName', worker.name);
    setValue('email', worker.email);
    setEmailSearchInput(worker.email);
  };

  const handleContinueToStep2 = () => {
    const photoIsRep = watch('photoIsRepresentative');
    if (!selectedWorker || photoIsRep === undefined) {
      showWarningNotification(
        'Please select a worker and answer if their photo is representative',
        { title: 'Missing information' }
      );
      return;
    }
    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const toggleEventType = (eventTypeId: number) => {
    const currentTypes = watchedEventTypes || [];
    if (currentTypes.includes(eventTypeId)) {
      setValue(
        'sharedEventsType',
        currentTypes.filter((id) => id !== eventTypeId)
      );
    } else {
      setValue('sharedEventsType', [...currentTypes, eventTypeId]);
    }
  };

  const toggleTrait = (traitId: number) => {
    const currentTraits = watchedTraits || [];
    const maxTraits = 4;

    if (currentTraits.includes(traitId)) {
      setValue(
        'endorsedTraits',
        currentTraits.filter((id) => id !== traitId)
      );
    } else if (currentTraits.length < maxTraits) {
      setValue('endorsedTraits', [...currentTraits, traitId]);
    } else {
      showWarningNotification(`You can select up to ${maxTraits} traits`, {
        title: 'Maximum traits reached',
      });
    }
  };

  const onSubmit = async (data: EndorsementFormData) => {
    try {
      setIsLoading(true);

      const payload: EndorsementPayload = {
        workerId: data.workerId || null,
        email: data.email,
        fullName: data.fullName,
        companyName: data.companyName,
        reviewDescription: data.reviewDescription || null,
        photoIsRepresentative: data.photoIsRepresentative ?? null,
        performance: data.performance || null,
        isEndorsedManager: data.isEndorsedManager,
        sharedEventsNumber: data.sharedEventsNumber,
        sharedEventsType: data.sharedEventsType,
        endorsedTraits: data.endorsedTraits,
      };

      await saveEndorsement(payload);
      showSuccessNotification('Endorsement submitted successfully!');
      onHide();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error submitting endorsement';
      showErrorNotification(message);
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalidSubmit = (formErrors: any) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length > 0) {
      showWarningNotification(
        `Please fill in all required fields: ${errorFields.join(', ')}`,
        { title: 'Form incomplete' }
      );
    }
  };

  const handleBrandSearch = async (query: string) => {
    if (query.length < 2) {
      setBrandSuggestions([]);
      return;
    }
    try {
      const response = await searchCommonBrands(query);
      const brands =
        response.suggestions?.map((s: { name: string }) => s.name) || [];
      setBrandSuggestions(brands);
    } catch (error) {
      console.error('Error searching brands:', error);
      setBrandSuggestions([]);
    }
  };

  return (
    <Modal
      opened={show}
      onClose={onHide}
      size="lg"
      centered
      title={
        <Flex align="center" gap="md">
          {step === 2 && !isMobile && (
            <ActionIcon variant="subtle" onClick={handleBackToStep1}>
              <HiArrowLeft size={24} color="black" />
            </ActionIcon>
          )}
          <Title order={3}>Endorse Worker</Title>
        </Flex>
      }
      closeButtonProps={isMobile && step === 1 ? { display: 'none' } : {}}
    >
      <LoadingOverlay visible={isLoading} />

      {isMobile && step === 1 && (
        <ActionIcon
          variant="subtle"
          onClick={onHide}
          style={{ position: 'absolute', top: 16, right: 16 }}
        >
          <IoMdClose size={24} />
        </ActionIcon>
      )}

      <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
        {step === 1 && (
          <Stack gap="md">
            <div>
              <Text size="sm" fw={500} mb={4}>
                Worker Name *
              </Text>
              <AsyncSelect
                cacheOptions
                onChange={(newValue) =>
                  handleWorkerSelect(newValue as SingleValue<WorkerOption>)
                }
                placeholder="Search by name..."
                value={
                  selectedWorker
                    ? { value: selectedWorker.id, label: selectedWorker.name }
                    : null
                }
                isClearable={!preselectedWorker}
                isDisabled={!!preselectedWorker}
              />
            </div>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Email *
              </Text>
              <Group gap="xs">
                <TextInput
                  type="email"
                  placeholder="Enter worker email"
                  value={emailSearchInput}
                  onChange={(e) => setEmailSearchInput(e.target.value)}
                  disabled={!!selectedWorker}
                  style={{ flex: 1 }}
                />
                <Button disabled={!!selectedWorker}>Search</Button>
              </Group>
            </div>

            {selectedWorker && (
              <>
                <Group gap="md" mt="md">
                  <Avatar
                    src={selectedWorker.profileImageUrl || defaultAvatar}
                    size={75}
                    radius="xl"
                  />
                  <div>
                    <Text size="lg" fw={600}>
                      {selectedWorker.name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {selectedWorker.email}
                    </Text>
                  </div>
                </Group>

                <div>
                  <Text size="sm" fw={500} mb={4}>
                    Does their picture fairly portray them? *
                  </Text>
                  <Controller
                    control={control}
                    name="photoIsRepresentative"
                    render={({ field }) => {
                      const getRadioValue = () => {
                        if (field.value === undefined) return '';
                        return field.value ? 'true' : 'false';
                      };

                      return (
                        <Radio.Group
                          value={getRadioValue()}
                          onChange={(value) => field.onChange(value === 'true')}
                        >
                          <Group mt="xs">
                            <Radio value="true" label="Yes" />
                            <Radio value="false" label="No" />
                          </Group>
                        </Radio.Group>
                      );
                    }}
                  />
                </div>
              </>
            )}
          </Stack>
        )}

        {step === 2 && selectedWorker && (
          <Stack gap="md">
            {isMobile && (
              <Group justify="space-between" mb="xs">
                <ActionIcon variant="subtle" onClick={handleBackToStep1}>
                  <HiArrowLeft size={24} />
                </ActionIcon>
                <ActionIcon variant="subtle" onClick={onHide}>
                  <IoMdClose size={24} />
                </ActionIcon>
              </Group>
            )}

            <Group gap="md">
              <Avatar
                src={selectedWorker.profileImageUrl || defaultAvatar}
                size={60}
                radius="xl"
              />
              <div>
                <Text size="md" fw={600}>
                  {selectedWorker.name}
                </Text>
                <Text size="sm" c="dimmed">
                  {selectedWorker.email}
                </Text>
              </div>
            </Group>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Did they manage a program for you? *
              </Text>
              <Controller
                control={control}
                name="isEndorsedManager"
                render={({ field }) => {
                  const getRadioValue = () => {
                    if (field.value === undefined) return '';
                    return field.value ? 'true' : 'false';
                  };

                  return (
                    <Radio.Group
                      value={getRadioValue()}
                      onChange={(value) => {
                        const boolValue = value === 'true';
                        field.onChange(boolValue);
                      }}
                    >
                      <Group mt="xs">
                        <Radio value="true" label="Yes" />
                        <Radio value="false" label="No" />
                      </Group>
                    </Radio.Group>
                  );
                }}
              />
            </div>

            <div>
              <Text size="sm" fw={500} mb={4}>
                Number of shared events *
              </Text>
              <Controller
                control={control}
                name="sharedEventsNumber"
                rules={{ required: true }}
                render={({ field }) => (
                  <Radio.Group value={field.value} onChange={field.onChange}>
                    <Group mt="xs">
                      {EVENTS_NUMBER_OPTIONS.map((option) => (
                        <Radio
                          key={option.value}
                          value={option.value}
                          label={option.label}
                        />
                      ))}
                    </Group>
                  </Radio.Group>
                )}
              />
            </div>

            {filtersData?.eventTypes && (
              <div>
                <Text size="sm" fw={500} mb={4}>
                  What type of events did you work with them?
                </Text>
                <Group gap="xs">
                  {filtersData.eventTypes.map((eventType) => (
                    <Badge
                      key={eventType.id}
                      color={
                        watchedEventTypes?.includes(eventType.id)
                          ? 'green'
                          : 'gray'
                      }
                      variant={
                        watchedEventTypes?.includes(eventType.id)
                          ? 'filled'
                          : 'light'
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleEventType(eventType.id)}
                    >
                      {eventType.name}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {filtersData?.traits && (
              <div>
                <Group align="center" mb={4}>
                  <Text size="sm" fw={500}>
                    Traits that describe them best (max 4)
                  </Text>
                  <Text size="xs" c="dimmed">
                    ({watchedTraits?.length || 0}/4 selected)
                  </Text>
                </Group>
                <Group gap="xs">
                  {filtersData.traits.map((trait) => (
                    <Badge
                      key={trait.id}
                      color={
                        watchedTraits?.includes(trait.id) ? 'green' : 'gray'
                      }
                      variant={
                        watchedTraits?.includes(trait.id) ? 'filled' : 'light'
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleTrait(trait.id)}
                    >
                      {trait.name}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            <Controller
              control={control}
              name="companyName"
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  label="Brands that you've worked on/for with this person *"
                  placeholder="Enter company/brand name"
                  data={brandSuggestions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    handleBrandSearch(value);
                  }}
                  error={
                    errors.companyName ? 'This field is required' : undefined
                  }
                  limit={10}
                />
              )}
            />

            <Textarea
              label="Review (optional)"
              placeholder="Share your experience working with this person..."
              {...register('reviewDescription')}
              maxLength={300}
              rows={3}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>
                Performance rating *
              </Text>
              <Controller
                control={control}
                name="performance"
                rules={{ required: 'Performance rating is required' }}
                render={({ field }) => (
                  <StarRatingButton
                    value={field.value || 0}
                    setValue={(val) => field.onChange(Number(val))}
                    hideLabels
                    allowChange
                    size={35}
                    gap={3}
                  />
                )}
              />
              {errors.performance && (
                <Text size="xs" c="red" mt={4}>
                  {errors.performance.message}
                </Text>
              )}
            </div>

            <Text size="sm" c="dimmed" fs="italic">
              Endorsements help workers build their professional reputation on
              Trusted Herd. Your endorsement will be visible on their profile.
            </Text>
          </Stack>
        )}

        <Group justify="flex-end" mt="xl">
          {step === 1 ? (
            <Button onClick={handleContinueToStep2}>Continue</Button>
          ) : (
            <>
              {!isMobile && (
                <Button variant="default" onClick={handleBackToStep1}>
                  Back
                </Button>
              )}
              <Button type="submit" disabled={isLoading}>
                Submit
              </Button>
            </>
          )}
        </Group>
      </form>
    </Modal>
  );
}

export default EndorsementModal;
